import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/api';

// Existing alert interface for backwards compatibility
interface NotificationAlert {
  id: string;
  type: 'message' | 'group_message';
  title: string;
  message: string;
  senderName: string;
  conversationId?: string;
  groupId?: number;
  messageId?: string;
  timestamp: number;
  urgencyLevel?: 'routine' | 'urgent' | 'critical';
  urgencyReason?: string;
  forceSound?: boolean;
}

interface AlertMatchPayload {
  alertIds?: string[];
  notificationIds?: string[];
  messageIds?: string[];
}

// New comprehensive notification interfaces
export interface NotificationSender {
  id: string;
  name: string;
  role: string;
  department: string;
  avatar?: string;
}

export interface NotificationContent {
  title: string;
  body: string;
  preview: string;
  attachments: Array<{
    type: 'image' | 'file' | 'voice';
    url: string;
    name: string;
  }>;
}

export interface NotificationMetadata {
  conversationId?: string;
  messageId?: string;
  groupId?: string;
  patientId?: string;
  [key: string]: any;
}

export interface NotificationItem {
  id: string;
  type: 'new_message' | 'message_reply' | 'group_mention' | 'message_acknowledgment' | 
        'urgent_message' | 'patient_feedback' | 'duty_change' | 'system_alert' | 
        'appointment_reminder' | 'file_shared' |
        'ai_tool_execution' | 'ai_health_alert' | 'ai_recommendation' | 
        'ai_status_update' | 'ai_escalation' | 'ai_insight' | 'ai_follow_up' | 'ai_delegation' | string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  sender: NotificationSender | null;
  content: NotificationContent;
  metadata: NotificationMetadata;
  timestamp: string;
  read_at?: string;
  archived_at?: string;
  deleted_at?: string;
  is_read: boolean;
  is_archived: boolean;
  is_deleted: boolean;
  available_actions: string[];
}

export interface NotificationFilters {
  type?: string[];
  priority?: string[];
  unread?: boolean;
  archived?: boolean;
  sender_name?: string;
  conversation_id?: string;
  group_id?: string;
  patient_id?: string;
  date_range?: {
    start: string;
    end: string;
  };
}

export interface NotificationPreferences {
  enable_desktop_notifications: boolean;
  enable_sound_alerts: boolean;
  enable_email_digest: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_exceptions: string[];
  channel_preferences: { [key: string]: any };
  type_preferences: { [key: string]: any };
  email_frequency: 'immediate' | 'hourly' | 'daily' | 'weekly' | 'never';
}

export interface NotificationSummary {
  total_count: number;
  unread_count: number;
  recent_count: number;
  by_type: { [key: string]: number };
  by_priority: { [key: string]: number };
}

interface NotificationState {
  // Legacy alerts for backwards compatibility
  activeAlerts: NotificationAlert[];
  
  // New notification center state
  notifications: NotificationItem[];
  unreadCount: number;
  filters: NotificationFilters;
  loading: boolean;
  error: string | null;
  lastFetch: string | null;
  
  // Preferences
  preferences: NotificationPreferences | null;
  preferencesLoading: boolean;
  
  // Summary
  summary: NotificationSummary | null;
  
  // UI state
  isNotificationCenterOpen: boolean;
  selectedNotifications: string[];
  dismissingNotificationIds: string[];
  viewMode: 'list' | 'grouped';
  sortBy: 'date' | 'priority' | 'type';
  sortOrder: 'asc' | 'desc';
}

type NotificationPayload = Partial<NotificationItem> & Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const initialNotificationPreferences: NotificationPreferences = {
  enable_desktop_notifications: true,
  enable_sound_alerts: true,
  enable_email_digest: false,
  quiet_hours_enabled: false,
  quiet_hours_exceptions: ['critical'],
  channel_preferences: {},
  type_preferences: {},
  email_frequency: 'never'
};

const initialState: NotificationState = {
  activeAlerts: [],
  notifications: [],
  unreadCount: 0,
  filters: {},
  loading: false,
  error: null,
  lastFetch: null,
  preferences: null,
  preferencesLoading: false,
  summary: null,
  isNotificationCenterOpen: false,
  selectedNotifications: [],
  dismissingNotificationIds: [],
  viewMode: 'list',
  sortBy: 'date',
  sortOrder: 'desc'
};

const getAuthSnapshot = (state: any) => ({
  isAuthenticated: state.auth?.isAuthenticated === true,
  hasUser: Boolean(state.auth?.user),
  validationState: state.auth?.validationState,
  username: state.auth?.user?.username || 'none'
});

const normalizeNotificationSender = (raw: NotificationPayload): NotificationSender | null => {
  const sender = raw.sender;
  if (sender && typeof sender === 'object') {
    return {
      id: String(sender.id ?? ''),
      name: sender.name || raw.sender_name || 'System',
      role: sender.role || raw.sender_role || '',
      department: sender.department || raw.sender_department || '',
      avatar: sender.avatar || undefined
    };
  }

  if (raw.sender_name) {
    return {
      id: raw.sender_id ? String(raw.sender_id) : '',
      name: raw.sender_name,
      role: raw.sender_role || '',
      department: raw.sender_department || '',
      avatar: undefined
    };
  }

  return null;
};

const normalizeNotificationContent = (raw: NotificationPayload): NotificationContent => {
  const content = (raw.content && typeof raw.content === 'object' ? raw.content : {}) as {
    title?: string;
    body?: string;
    preview?: string;
    attachments?: unknown;
  };
  const body = content.body || raw.body || '';

  return {
    title: content.title || raw.title || 'Notification',
    body,
    preview: content.preview || raw.preview || body,
    attachments: Array.isArray(content.attachments)
      ? content.attachments
      : Array.isArray(raw.attachments)
        ? raw.attachments
        : []
  };
};

const normalizeNotificationMetadata = (raw: NotificationPayload): NotificationMetadata => {
  const metadata = raw.metadata && typeof raw.metadata === 'object' ? { ...raw.metadata } : {};
  const extraMetadata = raw.extra_metadata && typeof raw.extra_metadata === 'object'
    ? raw.extra_metadata
    : {};

  const conversationId = metadata.conversationId ?? metadata.conversation_id ?? raw.conversation_id;
  const messageId = metadata.messageId ?? metadata.message_id ?? raw.message_id;
  const groupId = metadata.groupId ?? metadata.group_id ?? raw.group_id;
  const patientId = metadata.patientId ?? metadata.patient_id ?? raw.patient_id;
  const urgencyLevel =
    metadata.urgencyLevel ??
    metadata.urgency_level ??
    extraMetadata.urgencyLevel ??
    extraMetadata.urgency_level;
  const urgencyReason =
    metadata.urgencyReason ??
    metadata.urgency_reason ??
    extraMetadata.urgencyReason ??
    extraMetadata.urgency_reason;

  return {
    ...extraMetadata,
    ...metadata,
    conversationId: conversationId !== undefined && conversationId !== null ? String(conversationId) : undefined,
    messageId: messageId !== undefined && messageId !== null ? String(messageId) : undefined,
    groupId: groupId !== undefined && groupId !== null ? String(groupId) : undefined,
    patientId: patientId !== undefined && patientId !== null ? String(patientId) : undefined,
    urgencyLevel: urgencyLevel !== undefined && urgencyLevel !== null ? String(urgencyLevel) : undefined,
    urgencyReason: urgencyReason !== undefined && urgencyReason !== null ? String(urgencyReason) : undefined
  };
};

const normalizeAvailableActions = (raw: NotificationPayload): string[] => {
  if (!Array.isArray(raw.available_actions)) {
    return [];
  }

  return raw.available_actions
    .map((action: any) => {
      if (typeof action === 'string') {
        return action;
      }
      if (action && typeof action === 'object') {
        return action.action || action.id || action.name || '';
      }
      return '';
    })
    .filter((action: string) => action !== '');
};

export const normalizeNotificationItem = (raw: NotificationPayload): NotificationItem => {
  const timestamp = raw.timestamp || raw.created_at || raw.updated_at || new Date().toISOString();
  const readAt = raw.read_at || undefined;
  const archivedAt = raw.archived_at || undefined;
  const deletedAt = raw.deleted_at || undefined;

  return {
    id: String(raw.id ?? ''),
    type: String(raw.type || 'system_alert').toLowerCase(),
    priority: String(raw.priority || 'low').toLowerCase() as NotificationItem['priority'],
    sender: normalizeNotificationSender(raw),
    content: normalizeNotificationContent(raw),
    metadata: normalizeNotificationMetadata(raw),
    timestamp: String(timestamp),
    read_at: readAt,
    archived_at: archivedAt,
    deleted_at: deletedAt,
    is_read: typeof raw.is_read === 'boolean' ? raw.is_read : Boolean(readAt),
    is_archived: typeof raw.is_archived === 'boolean' ? raw.is_archived : Boolean(archivedAt),
    is_deleted: typeof raw.is_deleted === 'boolean' ? raw.is_deleted : Boolean(deletedAt),
    available_actions: normalizeAvailableActions(raw)
  };
};

const findNotificationIndex = (notifications: NotificationItem[], notification: NotificationItem) => {
  const incomingMessageId = notification.metadata?.messageId;
  return notifications.findIndex(existing =>
    existing.id === notification.id ||
    (
      !!incomingMessageId &&
      !!existing.metadata?.messageId &&
      existing.metadata.messageId === incomingMessageId
    )
  );
};

const getNotificationIdFromAlert = (alert: NotificationAlert): string | null => {
  if (alert.id.startsWith('notification-')) {
    return alert.id.replace('notification-', '');
  }
  if (alert.id.startsWith('msg_')) {
    return null;
  }
  return alert.id || null;
};

const alertMatchesKeys = (alert: NotificationAlert, payload: AlertMatchPayload) => {
  const alertIds = new Set(payload.alertIds || []);
  const notificationIds = new Set(payload.notificationIds || []);
  const messageIds = new Set(payload.messageIds || []);
  const normalizedAlertNotificationId = getNotificationIdFromAlert(alert);

  if (alertIds.has(alert.id)) {
    return true;
  }

  if (
    normalizedAlertNotificationId &&
    notificationIds.has(normalizedAlertNotificationId)
  ) {
    return true;
  }

  if (alert.messageId && messageIds.has(alert.messageId)) {
    return true;
  }

  if (alert.id.startsWith('msg_') && messageIds.has(alert.id.replace('msg_', ''))) {
    return true;
  }

  return false;
};

const removeNotificationFromState = (state: NotificationState, notificationId: string) => {
  const index = state.notifications.findIndex(n => n.id === notificationId);
  if (index !== -1) {
    if (!state.notifications[index].is_read) {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    }
    state.notifications.splice(index, 1);
  }
  state.selectedNotifications = state.selectedNotifications.filter(id => id !== notificationId);
};

// Async thunks for API calls

export const fetchNotifications = createAsyncThunk<
  { results: NotificationItem[]; count: number; next?: string; previous?: string },
  { filters?: NotificationFilters; page?: number; pageSize?: number } | undefined
>(
  'notifications/fetchNotifications',
  async (params: { filters?: NotificationFilters; page?: number; pageSize?: number } | undefined, { getState, rejectWithValue }) => {
    // Check authentication state first
    const state = getState() as any;
    const auth = getAuthSnapshot(state);
    
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[NOTIFICATION DEBUG] Auth state check:', {
        isAuthenticated: auth.isAuthenticated,
        hasUser: auth.hasUser,
        validationState: auth.validationState,
        user: auth.username
      });
    }
    
    // Cookie-authenticated sessions should not require a bearer token string.
    if (!auth.isAuthenticated || !auth.hasUser || auth.validationState !== 'valid') {
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('[NOTIFICATION DEBUG] Rejecting due to no authentication or validation pending');
      }
      return rejectWithValue('User not authenticated or validation pending');
    }
    
    try {
      // Use apiService which handles cookie-based authentication automatically
      const queryParams = new URLSearchParams();
      
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.pageSize) {
        queryParams.append('page_size', params.pageSize.toString());
      }
      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(v => queryParams.append(key, v.toString()));
            } else if (typeof value === 'object' && value.start && value.end) {
              queryParams.append(`${key}_start`, value.start);
              queryParams.append(`${key}_end`, value.end);
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }
      
      const url = `/notifications/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      return await apiService.get(url, { _skipAuthFailure: true });
    } catch (error: any) {
      // Fail gracefully without triggering global logout for background polling
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch notifications');
    }
  }
);

export const fetchNotificationSummary = createAsyncThunk<NotificationSummary>(
  'notifications/fetchSummary',
  async (_, { getState, rejectWithValue }) => {
    // Check authentication state first
    const state = getState() as any;
    const auth = getAuthSnapshot(state);
    if (!auth.isAuthenticated || !auth.hasUser || auth.validationState !== 'valid') {
      return rejectWithValue('User not authenticated or validation pending');
    }
    
    try {
      return await apiService.get('/notifications/summary/', { _skipAuthFailure: true });
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch notification summary');
    }
  }
);

export const markNotificationsAsRead = createAsyncThunk<
  { notificationIds: string[] },
  string[]
>(
  'notifications/markAsRead',
  async (notificationIds: string[], { getState, rejectWithValue }) => {
    // Check authentication state first
    const state = getState() as any;
    const auth = getAuthSnapshot(state);
    if (!auth.isAuthenticated || !auth.hasUser) {
      return rejectWithValue('User not authenticated');
    }
    
    try {
      await apiService.post('/notifications/mark_read/', { notification_ids: notificationIds }, { _skipAuthFailure: true });
      return { notificationIds };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to mark notifications as read');
    }
  }
);

export const performBulkAction = createAsyncThunk<
  { notificationIds: string[]; action: string; matchedNotifications: Array<{ id: string; messageId?: string }> },
  { notificationIds: string[]; action: string }
>(
  'notifications/bulkAction',
  async ({ notificationIds, action }: { notificationIds: string[], action: string }, { getState, rejectWithValue }) => {
    // Check authentication state first
    const state = getState() as any;
    const auth = getAuthSnapshot(state);
    if (!auth.isAuthenticated || !auth.hasUser) {
      return rejectWithValue('User not authenticated');
    }

    const matchedNotifications = (state.notifications?.notifications || [])
      .filter((notification: NotificationItem) => notificationIds.includes(notification.id))
      .map((notification: NotificationItem) => ({
        id: notification.id,
        messageId: notification.metadata?.messageId
      }));
    
    try {
      await apiService.post('/notifications/bulk_action/', {
        notification_ids: notificationIds,
        action: action
      }, { _skipAuthFailure: true });
      return { notificationIds, action, matchedNotifications };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || `Failed to perform ${action} action`);
    }
  }
);

export const dismissNotification = createAsyncThunk<
  { notificationId: string; messageId?: string },
  string
>(
  'notifications/dismissNotification',
  async (notificationId: string, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const auth = getAuthSnapshot(state);
    if (!auth.isAuthenticated || !auth.hasUser) {
      return rejectWithValue('User not authenticated');
    }

    const existingNotification = (state.notifications?.notifications || []).find(
      (notification: NotificationItem) => notification.id === notificationId
    );

    try {
      await apiService.post(`/notifications/${notificationId}/dismiss/`, {}, { _skipAuthFailure: true });
      return {
        notificationId,
        messageId: existingNotification?.metadata?.messageId
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to dismiss notification');
    }
  }
);

export const fetchNotificationPreferences = createAsyncThunk<NotificationPreferences>(
  'notifications/fetchPreferences',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const auth = getAuthSnapshot(state);
      
      // Only fetch if user is authenticated and validation is complete
      if (!auth.isAuthenticated || !auth.hasUser || auth.validationState !== 'valid') {
        return rejectWithValue('User not authenticated or validation pending');
      }
      
      return await apiService.get('/notifications/preferences/my_preferences/', { _skipAuthFailure: true });
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch notification preferences');
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk<
  NotificationPreferences,
  Partial<NotificationPreferences>
>(
  'notifications/updatePreferences',
  async (preferences: Partial<NotificationPreferences>, { getState }) => {
    try {
      const state = getState() as any;
      const auth = getAuthSnapshot(state);
      
      // Only update if user is authenticated
      if (!auth.isAuthenticated || !auth.hasUser) {
        throw new Error('User not authenticated');
      }
      
      return await apiService.put('/notifications/preferences/my_preferences/', preferences);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.message || 'Failed to update notification preferences');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Legacy alert reducers for backwards compatibility
    showNotificationAlert: (state, action: PayloadAction<NotificationAlert>) => {
      const existingIndex = state.activeAlerts.findIndex(
        alert =>
          alert.id === action.payload.id ||
          (
            !!alert.messageId &&
            !!action.payload.messageId &&
            alert.messageId === action.payload.messageId
          )
      );
      
      if (existingIndex === -1) {
        state.activeAlerts.unshift(action.payload);
        if (state.activeAlerts.length > 5) {
          state.activeAlerts.pop();
        }
      } else {
        }
    },
    
    dismissNotificationAlert: (state, action: PayloadAction<string>) => {
      state.activeAlerts = state.activeAlerts.filter(alert => alert.id !== action.payload);
    },

    dismissNotificationAlertsByKeys: (state, action: PayloadAction<AlertMatchPayload>) => {
      state.activeAlerts = state.activeAlerts.filter(
        alert => !alertMatchesKeys(alert, action.payload)
      );
    },
    
    clearAllNotificationAlerts: (state) => {
      state.activeAlerts = [];
    },
    
    clearNotificationAlert: (state) => {
      if (state.activeAlerts.length > 0) {
        state.activeAlerts.shift();
      }
    },
    
    // New notification center reducers
    addNotification: (state, action: PayloadAction<NotificationItem>) => {
      const notification = normalizeNotificationItem(action.payload as NotificationPayload);
      const existingIndex = findNotificationIndex(state.notifications, notification);
      if (existingIndex === -1) {
        state.notifications.unshift(notification);
        if (!notification.is_read) {
          state.unreadCount += 1;
        }
      } else {
        state.notifications[existingIndex] = notification;
      }
    },
    
    updateNotification: (state, action: PayloadAction<NotificationItem>) => {
      const notification = normalizeNotificationItem(action.payload as NotificationPayload);
      const index = findNotificationIndex(state.notifications, notification);
      if (index !== -1) {
        const wasUnread = !state.notifications[index].is_read;
        const isUnread = !notification.is_read;
        
        state.notifications[index] = notification;
        
        // Update unread count
        if (wasUnread && !isUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        } else if (!wasUnread && isUnread) {
          state.unreadCount += 1;
        }
      } else {
        state.notifications.unshift(notification);
        if (!notification.is_read) {
          state.unreadCount += 1;
        }
      }
    },
    
    removeNotification: (state, action: PayloadAction<string>) => {
      removeNotificationFromState(state, action.payload);
    },
    
    setNotifications: (state, action: PayloadAction<NotificationItem[]>) => {
      const notifications = action.payload.map(notification =>
        normalizeNotificationItem(notification as NotificationPayload)
      );
      state.notifications = notifications;
      state.unreadCount = notifications.filter(n => !n.is_read).length;
    },
    
    updateUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    
    setFilters: (state, action: PayloadAction<NotificationFilters>) => {
      state.filters = action.payload;
    },
    
    updateFilter: (state, action: PayloadAction<{ key: keyof NotificationFilters, value: string | boolean | null }>) => {
      (state.filters as any)[action.payload.key] = action.payload.value;
    },
    
    clearFilters: (state) => {
      state.filters = {};
    },
    
    toggleNotificationCenter: (state) => {
      state.isNotificationCenterOpen = !state.isNotificationCenterOpen;
    },
    
    openNotificationCenter: (state) => {
      state.isNotificationCenterOpen = true;
    },
    
    closeNotificationCenter: (state) => {
      state.isNotificationCenterOpen = false;
    },
    
    selectNotification: (state, action: PayloadAction<string>) => {
      if (!state.selectedNotifications.includes(action.payload)) {
        state.selectedNotifications.push(action.payload);
      }
    },
    
    deselectNotification: (state, action: PayloadAction<string>) => {
      state.selectedNotifications = state.selectedNotifications.filter(id => id !== action.payload);
    },
    
    selectAllNotifications: (state) => {
      state.selectedNotifications = state.notifications.map(n => n.id);
    },
    
    clearSelection: (state) => {
      state.selectedNotifications = [];
    },
    
    setViewMode: (state, action: PayloadAction<'list' | 'grouped'>) => {
      state.viewMode = action.payload;
    },
    
    setSorting: (state, action: PayloadAction<{ sortBy: 'date' | 'priority' | 'type', sortOrder?: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      if (action.payload.sortOrder) {
        state.sortOrder = action.payload.sortOrder;
      }
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    }
  },
  
  extraReducers: (builder) => {
    // Fetch notifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.loading = false;
      const rawNotifications = Array.isArray(action.payload) ? action.payload : action.payload.results || [];
      const notifications = rawNotifications.map((notification: NotificationPayload) =>
        normalizeNotificationItem(notification)
      );
      state.notifications = notifications;
      state.unreadCount = state.summary?.unread_count ?? notifications.filter((n: NotificationItem) => !n.is_read).length;
      state.lastFetch = new Date().toISOString();
    });
    
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.loading = false;
      // Don't show error for authentication issues - this is expected when user is not logged in or validation is pending
      const errorMessage = action.payload as string || action.error.message || '';
      if (!errorMessage.includes('User not authenticated') && !errorMessage.includes('validation pending')) {
        state.error = action.error.message || 'Failed to fetch notifications';
      } else {
        // Clear any previous error for auth-related rejections
        state.error = null;
      }
    });
    
    // Fetch summary
    builder.addCase(fetchNotificationSummary.fulfilled, (state, action) => {
      state.summary = action.payload;
      state.unreadCount = action.payload.unread_count;
    });
    
    // Mark as read
    builder.addCase(markNotificationsAsRead.fulfilled, (state, action) => {
      action.payload.notificationIds.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification && !notification.is_read) {
          notification.is_read = true;
          notification.read_at = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
    });
    
    // Bulk actions
    builder.addCase(performBulkAction.fulfilled, (state, action) => {
      const { notificationIds, action: actionType, matchedNotifications } = action.payload;
      
      notificationIds.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification) {
          if (actionType === 'mark_read' && !notification.is_read) {
            notification.is_read = true;
            notification.read_at = new Date().toISOString();
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          } else if (actionType === 'mark_unread' && notification.is_read) {
            notification.is_read = false;
            notification.read_at = undefined;
            state.unreadCount += 1;
          } else if (actionType === 'archive') {
            notification.is_archived = true;
            notification.archived_at = new Date().toISOString();
          } else if (actionType === 'delete') {
            removeNotificationFromState(state, id);
          }
        }
      });

      if (actionType === 'delete') {
        state.activeAlerts = state.activeAlerts.filter(alert => !alertMatchesKeys(alert, {
          notificationIds: matchedNotifications.map(notification => notification.id),
          messageIds: matchedNotifications
            .map(notification => notification.messageId)
            .filter((messageId): messageId is string => Boolean(messageId))
        }));
      }
      
      // Clear selection after bulk action
      state.selectedNotifications = [];
    });

    builder.addCase(dismissNotification.pending, (state, action) => {
      if (!state.dismissingNotificationIds.includes(action.meta.arg)) {
        state.dismissingNotificationIds.push(action.meta.arg);
      }
    });

    builder.addCase(dismissNotification.fulfilled, (state, action) => {
      removeNotificationFromState(state, action.payload.notificationId);
      state.activeAlerts = state.activeAlerts.filter(alert => !alertMatchesKeys(alert, {
        notificationIds: [action.payload.notificationId],
        messageIds: action.payload.messageId ? [action.payload.messageId] : []
      }));
      state.dismissingNotificationIds = state.dismissingNotificationIds.filter(
        id => id !== action.payload.notificationId
      );
    });

    builder.addCase(dismissNotification.rejected, (state, action) => {
      state.dismissingNotificationIds = state.dismissingNotificationIds.filter(
        id => id !== action.meta.arg
      );
      const errorMessage = action.payload as string || action.error.message || null;
      if (errorMessage && !String(errorMessage).includes('User not authenticated')) {
        state.error = String(errorMessage);
      }
    });
    
    // Preferences
    builder.addCase(fetchNotificationPreferences.pending, (state) => {
      state.preferencesLoading = true;
    });
    
    builder.addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
      state.preferencesLoading = false;
      state.preferences = action.payload;
    });
    
    builder.addCase(fetchNotificationPreferences.rejected, (state) => {
      state.preferencesLoading = false;
    });
    
    builder.addCase(updateNotificationPreferences.fulfilled, (state, action) => {
      state.preferences = action.payload;
    });
  }
});

export const {
  // Legacy actions
  showNotificationAlert,
  dismissNotificationAlert,
  dismissNotificationAlertsByKeys,
  clearNotificationAlert,
  clearAllNotificationAlerts,
  
  // New notification center actions
  addNotification,
  updateNotification,
  removeNotification,
  setNotifications,
  updateUnreadCount,
  setFilters,
  updateFilter,
  clearFilters,
  toggleNotificationCenter,
  openNotificationCenter,
  closeNotificationCenter,
  selectNotification,
  deselectNotification,
  selectAllNotifications,
  clearSelection,
  setViewMode,
  setSorting,
  setError,
  clearError
} = notificationSlice.actions;

export default notificationSlice.reducer;
